import { MIN_EVENT_MINUTES } from "../../../../../_shared/_logic/display-scale"
import type {
  BoardDropKind,
  BoardEvent,
  BoardLayoutConfig,
} from "../../../../../../../_logic/calendar-types"

export type CalendarAreaTarget = {
  area: HTMLElement
  columnId: string
  day: Date
  employeeId: string | null
  kind: BoardDropKind
  timeframeStart: Date | null
  timeframeEnd: Date | null
  softSlotOccupiedEventId: string | null
}

export const clampValue = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

export const snapToStep = (value: number, slotMinutes: number) =>
  Math.round(value / slotMinutes) * slotMinutes

export const addMinutesToDate = (value: Date, minutes: number) =>
  new Date(value.getTime() + minutes * 60 * 1000)

export const viewStartForDay = (day: Date, dayStartHour: number) => {
  const result = new Date(day)
  result.setHours(dayStartHour, 0, 0, 0)

  return result
}

export const minutesFromDate = (value: Date, day: Date, dayStartHour: number) => {
  const diff = (value.getTime() - viewStartForDay(day, dayStartHour).getTime()) / 60000

  return Math.max(0, diff)
}

export const minutesFromPointer = ({
  clientY,
  area,
  slotHeight,
  slotMinutes,
  slotCount,
}: {
  clientY: number
  area: HTMLElement | null
  slotHeight: number
  slotMinutes: number
  slotCount: number
}) => {
  if (!area) return null

  const rect = area.getBoundingClientRect()
  const relative = clientY - rect.top
  const maxHeight = slotCount * slotHeight
  const clampedY = clampValue(relative, 0, maxHeight)
  const minutes = (clampedY / slotHeight) * slotMinutes

  return snapToStep(minutes, slotMinutes)
}

const parseTimestampDate = (value?: string) => {
  if (!value) return null

  const parsed = Number(value)

  if (Number.isNaN(parsed)) return null

  const date = new Date(parsed)

  return Number.isNaN(date.getTime()) ? null : date
}

export const findCalendarAreaFromPoint = (
  clientX: number,
  clientY: number,
): CalendarAreaTarget | null => {
  const target = document.elementFromPoint(clientX, clientY)

  if (!target) return null

  const softArea = target.closest('[data-calendar-area="soft-slot"]') as HTMLElement | null

  if (softArea) {
    const columnId = softArea.dataset.columnId
    const day = parseTimestampDate(softArea.dataset.dayTs)
    const employeeId = softArea.dataset.employeeId || null
    const timeframeStart = parseTimestampDate(softArea.dataset.timeframeStartTs)
    const timeframeEnd = parseTimestampDate(softArea.dataset.timeframeEndTs)

    if (!columnId || !day || !timeframeStart || !timeframeEnd) return null

    return {
      area: softArea,
      columnId,
      day,
      employeeId,
      kind: "soft",
      timeframeStart,
      timeframeEnd,
      softSlotOccupiedEventId: softArea.dataset.softSlotOccupiedEventId || null,
    }
  }

  const area = target.closest('[data-calendar-area="events"]') as HTMLElement | null

  if (!area) return null

  const columnId = area.dataset.columnId
  const day = parseTimestampDate(area.dataset.dayTs)
  const employeeId = area.dataset.employeeId || null

  if (!columnId || !day) return null

  return {
    area,
    columnId,
    day,
    employeeId,
    kind: "hard",
    timeframeStart: null,
    timeframeEnd: null,
    softSlotOccupiedEventId: null,
  }
}

export const computeEventPosition = ({
  start,
  end,
  day,
  layout,
  totalMinutes,
}: {
  start: Date
  end: Date
  day: Date
  layout: BoardLayoutConfig
  totalMinutes: number
}) => {
  const viewStart = viewStartForDay(day, layout.dayStartHour)
  const viewEnd = addMinutesToDate(viewStart, totalMinutes)
  const overlapStart = start < viewStart ? viewStart : start
  const overlapEnd = end > viewEnd ? viewEnd : end
  const minutesFromStart = Math.max(0, (overlapStart.getTime() - viewStart.getTime()) / 60000)
  const duration = Math.max(0, (overlapEnd.getTime() - overlapStart.getTime()) / 60000)
  const top = (minutesFromStart / layout.slotMinutes) * layout.slotHeight
  const height = Math.max(
    layout.minEventHeight,
    (duration / layout.slotMinutes) * layout.slotHeight,
  )

  return { top, height }
}

export const computeResizeTimes = ({
  mode,
  pointerMinutes,
  originalStart,
  originalEnd,
  day,
  layout,
  totalMinutes,
}: {
  mode: "resize-start" | "resize-end"
  pointerMinutes: number
  originalStart: Date
  originalEnd: Date
  day: Date
  layout: BoardLayoutConfig
  totalMinutes: number
}) => {
  const baseStartMinutes = minutesFromDate(originalStart, day, layout.dayStartHour)
  const baseEndMinutes = minutesFromDate(originalEnd, day, layout.dayStartHour)
  const viewStart = viewStartForDay(day, layout.dayStartHour)

  if (mode === "resize-start") {
    const maxStart = Math.max(0, baseEndMinutes - MIN_EVENT_MINUTES)
    const nextMinutes = clampValue(pointerMinutes, 0, maxStart)

    return { start: addMinutesToDate(viewStart, nextMinutes), end: originalEnd }
  }

  const minEnd = baseStartMinutes + MIN_EVENT_MINUTES
  const nextMinutes = clampValue(pointerMinutes, minEnd, totalMinutes)

  return { start: originalStart, end: addMinutesToDate(viewStart, nextMinutes) }
}

export const computeMoveTimes = ({
  pointerMinutes,
  targetDay,
  offsetMinutes,
  durationMinutes,
  layout,
  totalMinutes,
}: {
  pointerMinutes: number
  targetDay: Date
  offsetMinutes: number
  durationMinutes: number
  layout: BoardLayoutConfig
  totalMinutes: number
}) => {
  const maxStart = Math.max(0, totalMinutes - durationMinutes)
  const rawStart = pointerMinutes - offsetMinutes
  const snappedStart = snapToStep(rawStart, layout.slotMinutes)
  const clampedStart = clampValue(snappedStart, 0, maxStart)
  const start = addMinutesToDate(viewStartForDay(targetDay, layout.dayStartHour), clampedStart)

  return { start, end: addMinutesToDate(start, durationMinutes) }
}

export const minutesFromSoftTargetPointer = ({
  clientY,
  area,
  targetDay,
  timeframeStart,
  layout,
}: {
  clientY: number
  area: HTMLElement | null
  targetDay: Date
  timeframeStart: Date
  layout: BoardLayoutConfig
}) => {
  if (!area) return null

  const rect = area.getBoundingClientRect()
  const relative = clientY - rect.top
  const clampedY = clampValue(relative, 0, Math.max(0, rect.height))
  const frameStartMinutes = minutesFromDate(timeframeStart, targetDay, layout.dayStartHour)
  const minutes = frameStartMinutes + (clampedY / layout.slotHeight) * layout.slotMinutes

  return snapToStep(minutes, layout.slotMinutes)
}

export const computeSoftMoveTimes = ({
  pointerMinutes,
  targetDay,
  offsetMinutes,
  durationMinutes,
  timeframeStart,
  timeframeEnd,
  layout,
}: {
  pointerMinutes: number
  targetDay: Date
  offsetMinutes: number
  durationMinutes: number
  timeframeStart: Date
  timeframeEnd: Date
  layout: BoardLayoutConfig
}) => {
  const frameStartMinutes = minutesFromDate(timeframeStart, targetDay, layout.dayStartHour)
  const frameEndMinutes = Math.max(
    frameStartMinutes + MIN_EVENT_MINUTES,
    minutesFromDate(timeframeEnd, targetDay, layout.dayStartHour),
  )
  const frameDuration = Math.max(MIN_EVENT_MINUTES, frameEndMinutes - frameStartMinutes)
  const rawStart = pointerMinutes - offsetMinutes
  const snappedStart = snapToStep(rawStart, layout.slotMinutes)
  const clampedStart =
    durationMinutes <= frameDuration
      ? clampValue(snappedStart, frameStartMinutes, frameEndMinutes - durationMinutes)
      : frameStartMinutes
  const start = addMinutesToDate(viewStartForDay(targetDay, layout.dayStartHour), clampedStart)
  const end = addMinutesToDate(start, durationMinutes)

  return {
    start,
    end,
    timeframeStart,
    timeframeEnd: durationMinutes <= frameDuration ? timeframeEnd : end,
  }
}

const numericDuration = (value: unknown) => {
  const parsed =
    typeof value === "number" ? value : typeof value === "string" ? Number(value) : null

  return parsed && Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

export const resolveTrueEventDurationMinutes = (
  item: Pick<BoardEvent, "start" | "end" | "workOrder">,
  fallbackMinutes: number,
) => {
  const eventDuration = Math.round((item.end.getTime() - item.start.getTime()) / 60000)

  if (Number.isFinite(eventDuration) && eventDuration > 0) return eventDuration

  const workOrderDuration = numericDuration(item.workOrder?.estimated_duration_min)

  if (workOrderDuration) return Math.round(workOrderDuration)

  return Math.max(1, fallbackMinutes)
}
